import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PortfolioPanel = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('3M');
  const [portfolioData, setPortfolioData] = useState({
    totalHoldings: 45250,
    totalValue: 3862350,
    averageCost: 82.15,
    unrealizedPnL: 142750,
    unrealizedPnLPercent: 3.84,
    exposureCoverage: 78.5,
    cashFlowImpact: -285000
  });

  const [positions, setPositions] = useState([
    {
      id: 1,
      date: '2024-09-15',
      type: 'Purchase',
      quantity: 15000,
      price: 81.25,
      value: 1218750,
      broker: 'ICE Futures',
      status: 'Settled'
    },
    {
      id: 2,
      date: '2024-09-10',
      type: 'Purchase',
      quantity: 12500,
      price: 83.40,
      value: 1042500,
      broker: 'EEX Exchange',
      status: 'Settled'
    },
    {
      id: 3,
      date: '2024-09-05',
      type: 'Purchase',
      quantity: 10000,
      price: 80.90,
      value: 809000,
      broker: 'ICE Futures',
      status: 'Settled'
    },
    {
      id: 4,
      date: '2024-08-28',
      type: 'Sale',
      quantity: -5000,
      price: 84.15,
      value: -420750,
      broker: 'Nodal Exchange',
      status: 'Settled'
    },
    {
      id: 5,
      date: '2024-08-20',
      type: 'Purchase',
      quantity: 12750,
      price: 79.85,
      value: 1018087,
      broker: 'EEX Exchange',
      status: 'Pending'
    }
  ]);

  const [demandForecast, setDemandForecast] = useState([
    { month: 'Oct 2024', demand: 8500, coverage: 85, shortfall: 1275 },
    { month: 'Nov 2024', demand: 9200, coverage: 72, shortfall: 2576 },
    { month: 'Dec 2024', demand: 7800, coverage: 95, shortfall: 390 },
    { month: 'Jan 2025', demand: 8900, coverage: 68, shortfall: 2848 },
    { month: 'Feb 2025', demand: 8400, coverage: 74, shortfall: 2184 },
    { month: 'Mar 2025', demand: 9600, coverage: 65, shortfall: 3360 }
  ]);

  const periods = ['1M', '3M', '6M', '12M'];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Settled': return 'text-success bg-success/10';
      case 'Pending': return 'text-warning bg-warning/10';
      case 'Failed': return 'text-error bg-error/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getCoverageColor = (coverage) => {
    if (coverage >= 90) return 'text-success';
    if (coverage >= 70) return 'text-warning';
    return 'text-error';
  };

  return (
    <div className="space-y-6">
      {/* Portfolio Overview */}
      <div className="bg-card rounded-lg border shadow-maritime-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-foreground">Portfolio Overview</h2>
          <div className="flex items-center space-x-2">
            {periods.map((period) => (
              <button
                key={period}
                onClick={() => setSelectedPeriod(period)}
                className={`px-3 py-1 rounded text-sm font-medium transition-maritime ${
                  selectedPeriod === period
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                {period}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-foreground">
              {formatNumber(portfolioData.totalHoldings)}
            </div>
            <div className="text-sm text-muted-foreground">Total EUA Holdings</div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-foreground">
              {formatCurrency(portfolioData.totalValue)}
            </div>
            <div className="text-sm text-muted-foreground">Portfolio Value</div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-foreground">
              €{portfolioData.averageCost.toFixed(2)}
            </div>
            <div className="text-sm text-muted-foreground">Average Cost</div>
          </div>
          
          <div className="text-center">
            <div className={`text-2xl font-bold ${
              portfolioData.unrealizedPnL >= 0 ? 'text-success' : 'text-error'
            }`}>
              {portfolioData.unrealizedPnL >= 0 ? '+' : ''}{formatCurrency(portfolioData.unrealizedPnL)}
            </div>
            <div className="text-sm text-muted-foreground">
              Unrealized P&L ({portfolioData.unrealizedPnLPercent >= 0 ? '+' : ''}{portfolioData.unrealizedPnLPercent.toFixed(2)}%)
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6 pt-6 border-t border-border">
          <div className="bg-muted/30 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">Exposure Coverage</span>
              <span className={`text-sm font-bold ${getCoverageColor(portfolioData.exposureCoverage)}`}>
                {portfolioData.exposureCoverage.toFixed(1)}%
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-500 ${
                  portfolioData.exposureCoverage >= 90 ? 'bg-success' :
                  portfolioData.exposureCoverage >= 70 ? 'bg-warning' : 'bg-error'
                }`}
                style={{ width: `${Math.min(portfolioData.exposureCoverage, 100)}%` }}
              />
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Fleet emissions coverage ratio
            </div>
          </div>

          <div className="bg-muted/30 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">Cash Flow Impact</span>
              <span className={`text-sm font-bold ${
                portfolioData.cashFlowImpact >= 0 ? 'text-success' : 'text-error'
              }`}>
                {formatCurrency(portfolioData.cashFlowImpact)}
              </span>
            </div>
            <div className="text-xs text-muted-foreground">
              Net impact on quarterly cash flow
            </div>
          </div>
        </div>
      </div>

      {/* Demand Forecast */}
      <div className="bg-card rounded-lg border shadow-maritime-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-foreground">EUA Demand Forecast</h3>
          <Button variant="outline" size="sm">
            <Icon name="Download" size={16} strokeWidth={2} className="mr-2" />
            Export Forecast
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Period</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Demand</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Coverage</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Shortfall</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
              </tr>
            </thead>
            <tbody>
              {demandForecast.map((forecast, index) => (
                <tr key={index} className="border-b border-border hover:bg-muted/30">
                  <td className="py-3 px-4 text-sm font-medium text-foreground">
                    {forecast.month}
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-mono">
                    {formatNumber(forecast.demand)}
                  </td>
                  <td className="py-3 px-4 text-sm text-right">
                    <span className={`font-medium ${getCoverageColor(forecast.coverage)}`}>
                      {forecast.coverage}%
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-mono text-error">
                    {formatNumber(forecast.shortfall)}
                  </td>
                  <td className="py-3 px-4 text-center">
                    {forecast.coverage >= 90 ? (
                      <Icon name="CheckCircle" size={16} className="text-success mx-auto" />
                    ) : forecast.coverage >= 70 ? (
                      <Icon name="AlertTriangle" size={16} className="text-warning mx-auto" />
                    ) : (
                      <Icon name="XCircle" size={16} className="text-error mx-auto" />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Positions */}
      <div className="bg-card rounded-lg border shadow-maritime-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-foreground">Recent Positions</h3>
          <Button variant="outline" size="sm">
            <Icon name="Eye" size={16} strokeWidth={2} className="mr-2" />
            View All
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Date</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Type</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Quantity</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Price</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Value</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Broker</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
              </tr>
            </thead>
            <tbody>
              {positions.map((position) => (
                <tr key={position.id} className="border-b border-border hover:bg-muted/30">
                  <td className="py-3 px-4 text-sm text-foreground">
                    {new Date(position.date).toLocaleDateString()}
                  </td>
                  <td className="py-3 px-4 text-sm">
                    <span className={`font-medium ${
                      position.type === 'Purchase' ? 'text-success' : 'text-error'
                    }`}>
                      {position.type}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-mono">
                    {position.quantity >= 0 ? '+' : ''}{formatNumber(position.quantity)}
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-mono">
                    €{position.price.toFixed(2)}
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-mono">
                    {formatCurrency(position.value)}
                  </td>
                  <td className="py-3 px-4 text-sm text-muted-foreground">
                    {position.broker}
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(position.status)}`}>
                      {position.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PortfolioPanel;